import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import "./CustomCursor.css";

const CustomCursor = () => {
  const [mousePosition, setMousePosition] = useState({ x: -100, y: -100 }); // Off-screen initially
  const [cursorVariant, setCursorVariant] = useState("default");
  const [isVisible, setIsVisible] = useState(false);
  const [isPlayerInactive, setIsPlayerInactive] = useState(false);

  useEffect(() => {
    const mouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY }); // Directly update state
      if (
        !isVisible &&
        !document.body.classList.contains("media-player-inactive")
      ) {
        setIsVisible(true);
      }
    };

    const mouseDown = () => setCursorVariant("click");
    const mouseUp = () => setCursorVariant("default");

    const handleHover = (e) => {
      const target = e.target;
      const isInteractive = target.closest("button, [data-interactive]");
      setCursorVariant(isInteractive ? "hover" : "default");
    };

    const mouseLeave = () => setIsVisible(false);
    const mouseEnter = () => setIsVisible(true);

    const checkMediaPlayerState = () => {
      setIsPlayerInactive(
        document.body.classList.contains("media-player-inactive")
      );
    };

    const bodyObserver = new MutationObserver(checkMediaPlayerState);
    bodyObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    checkMediaPlayerState();

    window.addEventListener("mousemove", mouseMove);
    window.addEventListener("mousedown", mouseDown);
    window.addEventListener("mouseup", mouseUp);
    window.addEventListener("mouseover", handleHover);
    document.documentElement.addEventListener("mouseleave", mouseLeave);
    document.documentElement.addEventListener("mouseenter", mouseEnter);

    return () => {
      window.removeEventListener("mousemove", mouseMove);
      window.removeEventListener("mousedown", mouseDown);
      window.removeEventListener("mouseup", mouseUp);
      window.removeEventListener("mouseover", handleHover);
      document.documentElement.removeEventListener("mouseleave", mouseLeave);
      document.documentElement.removeEventListener("mouseenter", mouseEnter);
      bodyObserver.disconnect();
    };
  }, [isVisible]);

  const variants = {
    default: {
      x: mousePosition.x - 8,
      y: mousePosition.y - 8,
      width: 16,
      height: 16,
      backgroundColor: "#ffffff",
      mixBlendMode: "difference",
      transition: {
        x: { type: "tween", ease: "easeOut", duration: 0.15 },
        y: { type: "tween", ease: "easeOut", duration: 0.15 },
      },
    },
    hover: {
      x: mousePosition.x - 16,
      y: mousePosition.y - 16,
      width: 32,
      height: 32,
      backgroundColor: "#ffffff",
      border: "2px solid #ffffff",
      mixBlendMode: "difference",
      transition: {
        x: { type: "tween", ease: "easeOut", duration: 0.15 },
        y: { type: "tween", ease: "easeOut", duration: 0.15 },
      },
    },
    click: {
      x: mousePosition.x - 12,
      y: mousePosition.y - 12,
      width: 24,
      height: 24,
      backgroundColor: "#ff5555",
      mixBlendMode: "difference",
      transition: {
        x: { type: "tween", ease: "easeOut", duration: 0.15 },
        y: { type: "tween", ease: "easeOut", duration: 0.15 },
      },
    },
  };

  if (isPlayerInactive) {
    return null;
  }

  return (
    <motion.div
      className="custom-cursor fixed top-0 left-0 rounded-full pointer-events-none z-[9999]"
      variants={variants}
      animate={cursorVariant}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        borderRadius: "50%",
        pointerEvents: "none",
        zIndex: 10001,
        opacity: isVisible ? 1 : 0,
      }}
    >
      <motion.div
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          border: "1px solid rgba(255, 255, 255, 0.5)",
        }}
        animate={
          cursorVariant === "hover"
            ? { scale: 1.5, opacity: 0.5 }
            : { scale: 1, opacity: 0 }
        }
        transition={{ duration: 0.2 }}
      />
    </motion.div>
  );
};

export default CustomCursor;
